import {FC, ReactNode, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET, POST, PUT} from '../../../services/api'
import Swal from 'sweetalert2'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {CardInfoGuide, CardInfoContent} from '../../../components/CardInfoGuide/CardInfoGuide'
import styles from './hotelpoints.module.css'
import {
  accumulationRateInfo,
  accumulationSourcesInfo,
  accumulationExpirationInfo,
  earningLimitsInfo,
  marginProtectionInfo,
  completedStatusInfo,
  redemptionMinimumsInfo,
  redemptionRulesInfo,
  antifraudCooldownInfo,
  walletCapInfo,
} from './components/hotelPointsCardInfoContent'

interface HotelPointsConfigData {
  id: number | null
  basePointsEnabled: boolean
  basePoints: number
  onlySpecificRules: boolean
  earnOnService: boolean
  earnOnExperience: boolean
  earnOnActivity: boolean
  earnOnTour: boolean
  earnOnSurvey: boolean
  earnOnFeedback: boolean
  earnOnReview: boolean
  earnOnRoomService: boolean
  requireCompletedStatus: boolean
  availableStatusSlugs: string[]
  cooldownDays: number
  creditExpirationDays: number
  maxPointsPerAction: number
  maxPointsPerStay: number
  maxWalletBalance: number
  minPointsToRedeem: number
  requireLoggedCustomer: boolean
  requireActiveReservation: boolean
  allowRedeemAfterCheckout: boolean
  redemptionRequiresApproval: boolean
}

type HotelPointsConfigPayload = Omit<HotelPointsConfigData, 'id'>

const DEFAULT_CONFIG: HotelPointsConfigData = {
  id: null,
  basePointsEnabled: true,
  basePoints: 0,
  onlySpecificRules: true,
  earnOnService: true,
  earnOnExperience: true,
  earnOnActivity: true,
  earnOnTour: true,
  earnOnSurvey: true,
  earnOnFeedback: true,
  earnOnReview: true,
  earnOnRoomService: true,
  requireCompletedStatus: true,
  availableStatusSlugs: ['completed'],
  cooldownDays: 0,
  creditExpirationDays: 0,
  maxPointsPerAction: 0,
  maxPointsPerStay: 0,
  maxWalletBalance: 0,
  minPointsToRedeem: 0,
  requireLoggedCustomer: true,
  requireActiveReservation: false,
  allowRedeemAfterCheckout: true,
  redemptionRequiresApproval: true,
}

const toNumber = (value: any, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const toInt = (value: any, fallback: number) => Math.max(0, Math.floor(toNumber(value, fallback)))

type HotelStatusOption = {
  id: number
  name: string
  slug: string
  status?: boolean
}

const normalizeHotelPointsConfig = (payload: any): HotelPointsConfigData => ({
  id: payload?.id ?? null,
  basePointsEnabled:
    payload?.basePointsEnabled !== undefined
      ? !!payload.basePointsEnabled
      : DEFAULT_CONFIG.basePointsEnabled,
  basePoints: toInt(payload?.basePoints, DEFAULT_CONFIG.basePoints),
  onlySpecificRules:
    payload?.onlySpecificRules !== undefined
      ? !!payload.onlySpecificRules
      : DEFAULT_CONFIG.onlySpecificRules,
  earnOnService:
    payload?.earnOnService !== undefined ? !!payload.earnOnService : DEFAULT_CONFIG.earnOnService,
  earnOnExperience:
    payload?.earnOnExperience !== undefined
      ? !!payload.earnOnExperience
      : DEFAULT_CONFIG.earnOnExperience,
  earnOnActivity:
    payload?.earnOnActivity !== undefined
      ? !!payload.earnOnActivity
      : DEFAULT_CONFIG.earnOnActivity,
  earnOnTour:
    payload?.earnOnTour !== undefined ? !!payload.earnOnTour : DEFAULT_CONFIG.earnOnTour,
  earnOnSurvey:
    payload?.earnOnSurvey !== undefined ? !!payload.earnOnSurvey : DEFAULT_CONFIG.earnOnSurvey,
  earnOnFeedback:
    payload?.earnOnFeedback !== undefined
      ? !!payload.earnOnFeedback
      : DEFAULT_CONFIG.earnOnFeedback,
  earnOnReview:
    payload?.earnOnReview !== undefined ? !!payload.earnOnReview : DEFAULT_CONFIG.earnOnReview,
  earnOnRoomService:
    payload?.earnOnRoomService !== undefined
      ? !!payload.earnOnRoomService
      : DEFAULT_CONFIG.earnOnRoomService,
  requireCompletedStatus:
    payload?.requireCompletedStatus !== undefined
      ? !!payload.requireCompletedStatus
      : DEFAULT_CONFIG.requireCompletedStatus,
  availableStatusSlugs: Array.isArray(payload?.availableStatusSlugs)
    ? payload.availableStatusSlugs.filter(Boolean)
    : DEFAULT_CONFIG.availableStatusSlugs,
  cooldownDays: toInt(payload?.cooldownDays, DEFAULT_CONFIG.cooldownDays),
  creditExpirationDays: toInt(
    payload?.creditExpirationDays,
    DEFAULT_CONFIG.creditExpirationDays
  ),
  maxPointsPerAction: toInt(payload?.maxPointsPerAction, DEFAULT_CONFIG.maxPointsPerAction),
  maxPointsPerStay: toInt(payload?.maxPointsPerStay, DEFAULT_CONFIG.maxPointsPerStay),
  maxWalletBalance: toInt(payload?.maxWalletBalance, DEFAULT_CONFIG.maxWalletBalance),
  minPointsToRedeem: toInt(payload?.minPointsToRedeem, DEFAULT_CONFIG.minPointsToRedeem),
  requireLoggedCustomer:
    payload?.requireLoggedCustomer !== undefined
      ? !!payload.requireLoggedCustomer
      : DEFAULT_CONFIG.requireLoggedCustomer,
  requireActiveReservation:
    payload?.requireActiveReservation !== undefined
      ? !!payload.requireActiveReservation
      : DEFAULT_CONFIG.requireActiveReservation,
  allowRedeemAfterCheckout:
    payload?.allowRedeemAfterCheckout !== undefined
      ? !!payload.allowRedeemAfterCheckout
      : DEFAULT_CONFIG.allowRedeemAfterCheckout,
  redemptionRequiresApproval:
    payload?.redemptionRequiresApproval !== undefined
      ? !!payload.redemptionRequiresApproval
      : DEFAULT_CONFIG.redemptionRequiresApproval,
})

const buildHotelPointsConfigPayload = (
  config: HotelPointsConfigData
): HotelPointsConfigPayload => ({
  basePointsEnabled: config.basePointsEnabled,
  basePoints: config.basePoints,
  onlySpecificRules: config.onlySpecificRules,
  earnOnService: config.earnOnService,
  earnOnExperience: config.earnOnExperience,
  earnOnActivity: config.earnOnActivity,
  earnOnTour: config.earnOnTour,
  earnOnSurvey: config.earnOnSurvey,
  earnOnFeedback: config.earnOnFeedback,
  earnOnReview: config.earnOnReview,
  earnOnRoomService: config.earnOnRoomService,
  requireCompletedStatus: config.requireCompletedStatus,
  availableStatusSlugs: config.availableStatusSlugs,
  cooldownDays: config.cooldownDays,
  creditExpirationDays: config.creditExpirationDays,
  maxPointsPerAction: config.maxPointsPerAction,
  maxPointsPerStay: config.maxPointsPerStay,
  maxWalletBalance: config.maxWalletBalance,
  minPointsToRedeem: config.minPointsToRedeem,
  requireLoggedCustomer: config.requireLoggedCustomer,
  requireActiveReservation: config.requireActiveReservation,
  allowRedeemAfterCheckout: config.allowRedeemAfterCheckout,
  redemptionRequiresApproval: config.redemptionRequiresApproval,
})

type CollapsibleCardProps = {
  title: string
  children: ReactNode
  defaultOpen?: boolean
  infoContent?: CardInfoContent
  infoInteraction?: 'click' | 'hover' | 'both'
}

const CollapsibleCard: FC<CollapsibleCardProps> = ({
  title,
  children,
  defaultOpen = true,
  infoContent,
  infoInteraction = 'click',
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className='card pb-5'>
      <div className='card-header py-5 d-flex justify-content-between align-items-center'>
        <h3 className='card-title align-items-start flex-column mb-0'>
          <span className='card-label fw-bold fs-4 mb-1'>{title}</span>
        </h3>
        <div className='d-flex align-items-center gap-2'>
          {infoContent && <CardInfoGuide content={infoContent} interaction={infoInteraction} />}
          <button
            type='button'
            className='btn btn-sm btn-icon cashback-card-toggle'
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Fechar card' : 'Abrir card'}
          >
            <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
          </button>
        </div>
      </div>
      <div
        style={{
          maxHeight: isOpen ? '2800px' : '0px',
          opacity: isOpen ? 1 : 0,
          overflow: isOpen ? 'visible' : 'hidden',
          transition: 'max-height 0.35s ease, opacity 0.25s ease',
        }}
      >
        <div className='px-10 py-5'>{children}</div>
      </div>
    </div>
  )
}

const Content: FC = () => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('#tab1')
  const [config, setConfig] = useState<HotelPointsConfigData>(DEFAULT_CONFIG)
  const [hotelStatuses, setHotelStatuses] = useState<HotelStatusOption[]>([])
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [tabErrors, setTabErrors] = useState<{[key: string]: boolean}>({
    tab1: false,
    tab2: false,
    tab3: false,
  })

  const setField = <K extends keyof HotelPointsConfigData>(
    field: K,
    value: HotelPointsConfigData[K]
  ) => {
    setConfig((prev) => ({...prev, [field]: value}))
    if (errors[field as string]) {
      const next = {...errors}
      delete next[field as string]
      setErrors(next)
    }
  }

  const setIntField = (
    field:
      | 'basePoints'
      | 'cooldownDays'
      | 'creditExpirationDays'
      | 'maxPointsPerAction'
      | 'maxPointsPerStay'
      | 'maxWalletBalance'
      | 'minPointsToRedeem',
    value: string | number
  ) => {
    setField(field, toInt(value, 0))
  }

  const setBoolField = (field: keyof HotelPointsConfigData, value: string | boolean) => {
    setField(field, (value === 'true' || value === true) as HotelPointsConfigData[typeof field])
  }

  const getTabClass = (tabId: string, isActive: boolean) => {
    const baseClass = styles.tabItem
    if (tabErrors[tabId]) {
      return `${baseClass} ${styles.tabError} ${isActive ? 'active' : ''}`
    }
    return `${baseClass} ${isActive ? styles.tabActive : styles.tabInactive}`
  }

  const toggleStatusSlug = (slug: string) => {
    setConfig((prev) => {
      const selected = prev.availableStatusSlugs.includes(slug)
      const availableStatusSlugs = selected
        ? prev.availableStatusSlugs.filter((item) => item !== slug)
        : [...prev.availableStatusSlugs, slug]
      return {...prev, availableStatusSlugs}
    })
    if (errors.availableStatusSlugs) {
      const next = {...errors}
      delete next.availableStatusSlugs
      setErrors(next)
    }
  }

  const getHotelStatuses = async () => {
    try {
      const res = await POST('/status-hotel/filter', {status: true})
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.dados)
        ? res.dados
        : []
      setHotelStatuses(
        list
          .filter((item: any) => item?.slug)
          .map((item: any) => ({
            id: Number(item.id),
            name: item.name || item.slug,
            slug: String(item.slug),
            status: item.status,
          }))
      )
    } catch (error) {
      console.error('Erro ao carregar status do hotel:', error)
      setHotelStatuses([])
    }
  }

  const getConfig = async () => {
    try {
      const res = await GET('/hotel-points-config')
      const currentConfig = Array.isArray(res?.data)
        ? res.data[0]
        : Array.isArray(res?.data?.data)
        ? res.data.data[0]
        : res?.data?.data ?? res?.data

      if (currentConfig) {
        const normalized = normalizeHotelPointsConfig(currentConfig)
        setConfig(normalized)
      } else {
        setConfig(DEFAULT_CONFIG)
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de Pontos Hotel:', error)
    }
  }

  const init = async () => {
    try {
      setLoading(true)
      await Promise.all([getConfig(), getHotelStatuses()])
    } catch (error) {
      console.error('Erro ao inicializar:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateForm = () => {
    const next: {[k: string]: string} = {}
    const nextTabErrors = {tab1: false, tab2: false, tab3: false}

    if (config.basePointsEnabled && config.basePoints < 0) {
      next['basePoints'] = 'Informe um número inteiro igual ou maior que zero'
      nextTabErrors.tab1 = true
    }

    if (config.creditExpirationDays < 0) {
      next['creditExpirationDays'] = 'Informe um número de dias igual ou maior que zero'
      nextTabErrors.tab1 = true
    }

    if (config.maxPointsPerAction < 0) {
      next['maxPointsPerAction'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab1 = true
    }

    if (config.maxPointsPerStay < 0) {
      next['maxPointsPerStay'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab1 = true
    }

    if (config.minPointsToRedeem < 0) {
      next['minPointsToRedeem'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab2 = true
    }

    if (config.cooldownDays < 0) {
      next['cooldownDays'] = 'Informe um número de dias igual ou maior que zero'
      nextTabErrors.tab3 = true
    }

    if (config.maxWalletBalance < 0) {
      next['maxWalletBalance'] = 'Informe um valor igual ou maior que zero'
      nextTabErrors.tab3 = true
    }

    if (config.requireCompletedStatus && config.availableStatusSlugs.length === 0) {
      next['availableStatusSlugs'] = 'Selecione ao menos um status para liberar os pontos'
      nextTabErrors.tab1 = true
    }

    setErrors(next)
    setTabErrors(nextTabErrors)
    return Object.keys(next).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: 'Verifique os campos destacados antes de salvar.',
      })
      return
    }

    try {
      setLoading(true)
      const payload = buildHotelPointsConfigPayload(config)
      if (config.id) {
        const res = await PUT(`/hotel-points-config/${config.id}`, payload)
        if (res?.success === false) {
          throw new Error(res?.message || 'Erro ao atualizar configurações de Pontos Hotel')
        }
      } else {
        const res = await POST('/hotel-points-config', payload)
        if (res?.success === false) {
          throw new Error(res?.message || 'Erro ao criar configurações de Pontos Hotel')
        }

        const createdConfig = res?.data ?? res?.dados ?? null
        if (createdConfig?.id) setConfig((prev) => ({...prev, id: createdConfig.id}))
      }

      Swal.fire({
        icon: 'success',
        title: 'Configurações salvas',
        text: 'As configurações foram salvas com sucesso!',
      })
    } catch (error) {
      console.error('Erro ao salvar configurações de Pontos Hotel:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) || 'Erro ao salvar as configurações. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const yesNoOptions = [
    {select: 'Sim', value: true},
    {select: 'Não', value: false},
  ]

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Configurações</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Defina as regras de acúmulo, resgate e proteção do seu programa de Pontos Hotel
                </span>
              </h3>
            </div>

            <div className='card-body'>
              <div className='d-flex justify-content-between'>
                <div className={`d-flex ${styles.tabMenu}`}>
                  <div
                    className={getTabClass('tab1', activeTab === '#tab1')}
                    onClick={() => setActiveTab('#tab1')}
                  >
                    <i className='fas fa-coins me-2'></i>
                    Acúmulo
                  </div>
                  <div
                    className={getTabClass('tab2', activeTab === '#tab2')}
                    onClick={() => setActiveTab('#tab2')}
                  >
                    <i className='fas fa-gift me-2'></i>
                    Resgate
                  </div>
                  <div
                    className={getTabClass('tab3', activeTab === '#tab3')}
                    onClick={() => setActiveTab('#tab3')}
                  >
                    <i className='fas fa-shield-alt me-2'></i>
                    Blindagem
                  </div>
                </div>
              </div>

              <div>
                {activeTab === '#tab1' && (
                  <div>
                    <CollapsibleCard title='Pontos Base' infoContent={accumulationRateInfo}>
                      <div className='row g-4 align-items-end'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Pontos base ativos'
                            value={config.basePointsEnabled}
                            change={(value: string | boolean) => {
                              const isEnabled = value === 'true' || value === true
                              setField('basePointsEnabled', isEnabled)
                              if (!isEnabled) setField('basePoints', 0)
                            }}
                            options={yesNoOptions}
                            required
                          />
                        </div>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Pontos base'
                            value={String(config.basePoints)}
                            change={(value: string) => setIntField('basePoints', value)}
                            required={config.basePointsEnabled}
                            disabled={!config.basePointsEnabled}
                            minNumber={0}
                            allowFloat={false}
                          />
                          {errors['basePoints'] && (
                            <div className='text-danger small mt-1'>{errors['basePoints']}</div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Origens de Acúmulo'
                      infoContent={accumulationSourcesInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Serviço'
                            value={config.earnOnService}
                            change={(value: string | boolean) => setBoolField('earnOnService', value)}
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Experiência'
                            value={config.earnOnExperience}
                            change={(value: string | boolean) =>
                              setBoolField('earnOnExperience', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Atividade'
                            value={config.earnOnActivity}
                            change={(value: string | boolean) =>
                              setBoolField('earnOnActivity', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Tour'
                            value={config.earnOnTour}
                            change={(value: string | boolean) => setBoolField('earnOnTour', value)}
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        {/* Pesquisa (survey): não usamos no hotel
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Pesquisa'
                            value={config.earnOnSurvey}
                            change={(value: string | boolean) => setBoolField('earnOnSurvey', value)}
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        */}
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Pesquisa'
                            value={config.earnOnFeedback}
                            change={(value: string | boolean) =>
                              setBoolField('earnOnFeedback', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Opinião'
                            value={config.earnOnReview}
                            change={(value: string | boolean) => setBoolField('earnOnReview', value)}
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-3'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Serviços Essenciais'
                            value={config.earnOnRoomService}
                            change={(value: string | boolean) =>
                              setBoolField('earnOnRoomService', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard title='Proteção' infoContent={marginProtectionInfo}>
                      <div className='row g-4'>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Gerar pontos apenas para itens com regras cadastradas?'
                            value={config.onlySpecificRules}
                            change={(value: string | boolean) =>
                              setBoolField('onlySpecificRules', value)
                            }
                            options={yesNoOptions}
                            required
                            marginTop='0'
                          />
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Status para Liberação'
                      infoContent={completedStatusInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Exigir status concluído'
                            value={config.requireCompletedStatus}
                            change={(value: string | boolean) =>
                              setBoolField('requireCompletedStatus', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-12'>
                          <label className='form-label fw-normal'>
                            Status ativos que liberam os pontos
                          </label>
                          {hotelStatuses.length === 0 ? (
                            <div className='text-muted small'>
                              Nenhum status ativo encontrado. Cadastre status do hotel para
                              selecioná-los aqui.
                            </div>
                          ) : (
                            <div className={styles.statusCheckboxGrid}>
                              {hotelStatuses.map((statusItem) => {
                                const checked = config.availableStatusSlugs.includes(
                                  statusItem.slug
                                )
                                return (
                                  <label
                                    key={statusItem.id || statusItem.slug}
                                    className={`${styles.statusCheckboxItem} ${
                                      checked ? styles.statusCheckboxItemActive : ''
                                    }`}
                                  >
                                    <input
                                      type='checkbox'
                                      className='form-check-input mt-1'
                                      checked={checked}
                                      onChange={() => toggleStatusSlug(statusItem.slug)}
                                    />
                                    <span>
                                      <span className='d-block fw-semibold text-gray-800'>
                                        {statusItem.name}
                                      </span>
                                      <span className='d-block text-muted small'>
                                        {statusItem.slug}
                                      </span>
                                    </span>
                                  </label>
                                )
                              })}
                            </div>
                          )}
                          {errors['availableStatusSlugs'] && (
                            <div className='text-danger small mt-2'>
                              {errors['availableStatusSlugs']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Limites de Acúmulo'
                      infoContent={earningLimitsInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Teto de pontos por ação'
                            value={String(config.maxPointsPerAction)}
                            change={(value: string) => setIntField('maxPointsPerAction', value)}
                            minNumber={0}
                            allowFloat={false}
                            marginTop='0'
                          />
                          {errors['maxPointsPerAction'] && (
                            <div className='text-danger small mt-1'>
                              {errors['maxPointsPerAction']}
                            </div>
                          )}
                        </div>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Teto de pontos por estadia'
                            value={String(config.maxPointsPerStay)}
                            change={(value: string) => setIntField('maxPointsPerStay', value)}
                            minNumber={0}
                            allowFloat={false}
                            marginTop='0'
                          />
                          {errors['maxPointsPerStay'] && (
                            <div className='text-danger small mt-1'>
                              {errors['maxPointsPerStay']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard
                      title='Validade do Crédito'
                      infoContent={accumulationExpirationInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Dias até o saldo expirar'
                            value={String(config.creditExpirationDays)}
                            change={(value: string) =>
                              setIntField('creditExpirationDays', value)
                            }
                            minNumber={0}
                            allowFloat={false}
                          />
                          {errors['creditExpirationDays'] && (
                            <div className='text-danger small mt-1'>
                              {errors['creditExpirationDays']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>
                  </div>
                )}

                {activeTab === '#tab2' && (
                  <div>
                    <CollapsibleCard
                      title='Mínimo para Resgate'
                      infoContent={redemptionMinimumsInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Pontos mínimos para resgate'
                            value={String(config.minPointsToRedeem)}
                            change={(value: string) => setIntField('minPointsToRedeem', value)}
                            minNumber={0}
                            allowFloat={false}
                          />
                          {errors['minPointsToRedeem'] && (
                            <div className='text-danger small mt-1'>
                              {errors['minPointsToRedeem']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard title='Regras de Resgate' infoContent={redemptionRulesInfo}>
                      <div className='row g-4'>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Exigir cliente logado'
                            value={config.requireLoggedCustomer}
                            change={(value: string | boolean) =>
                              setBoolField('requireLoggedCustomer', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Exigir reserva ativa'
                            value={config.requireActiveReservation}
                            change={(value: string | boolean) =>
                              setBoolField('requireActiveReservation', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Permitir resgate após checkout'
                            value={config.allowRedeemAfterCheckout}
                            change={(value: string | boolean) =>
                              setBoolField('allowRedeemAfterCheckout', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                        <div className='col-md-6'>
                          <Input
                            inputAttr={{type: 'select'}}
                            label='Resgate exige aprovação da equipe'
                            value={config.redemptionRequiresApproval}
                            change={(value: string | boolean) =>
                              setBoolField('redemptionRequiresApproval', value)
                            }
                            options={yesNoOptions}
                            marginTop='0'
                          />
                        </div>
                      </div>
                    </CollapsibleCard>
                  </div>
                )}

                {activeTab === '#tab3' && (
                  <div>
                    <CollapsibleCard
                      title='Carência de Liberação'
                      infoContent={antifraudCooldownInfo}
                    >
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Dias de carência até liberar o saldo'
                            value={String(config.cooldownDays)}
                            change={(value: string) => setIntField('cooldownDays', value)}
                            minNumber={0}
                            allowFloat={false}
                          />
                          {errors['cooldownDays'] && (
                            <div className='text-danger small mt-1'>{errors['cooldownDays']}</div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>

                    <CollapsibleCard title='Limite da Carteira' infoContent={walletCapInfo}>
                      <div className='row g-4'>
                        <div className='col-md-4'>
                          <Input
                            inputAttr={{type: 'number'}}
                            label='Saldo máximo na carteira'
                            value={String(config.maxWalletBalance)}
                            change={(value: string) => setIntField('maxWalletBalance', value)}
                            minNumber={0}
                            allowFloat={false}
                          />
                          {errors['maxWalletBalance'] && (
                            <div className='text-danger small mt-1'>
                              {errors['maxWalletBalance']}
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleCard>
                  </div>
                )}
              </div>
            </div>

            <div className='card-footer border-0 pt-0 d-flex justify-content-end py-6 px-9'>
              <Button
                text='Salvar'
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                disabled={loading}
                btnClass='fw-bolder'
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const HotelPointsConfig: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Pontos Hotel', path: 'hotel-points', isActive: false},
          {title: 'Configurações', path: 'hotel-points/config', isActive: true},
        ]}
      >
        Configurações
      </PageTitle>
      <Content />
    </>
  )
}

export {HotelPointsConfig}
